import { debugLog } from '@repo/common-utils/Logger';
import { getStep } from './checkout-step';
import {
    AddToCartEvent,
    EnhancedConversionCustomer,
    EventProduct,
    RemoveFromCartEvent,
    TotalPrice,
    Currency,
    StartCheckoutEvent,
    PaymentInfoAddedEvent,
    BookEvent,
    BilberryWidgetEvent,
    ViewItemEvent,
    CheckoutStepEvent,
    AuthenticationEvent,
} from './events.types';

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[]; // Google Tag Manager
        gtag?: (type: 'event', eventName: string, data: Record<string, unknown>) => void;
    }
}

const mapPrice =
    (product: EventProduct, currency: Currency, promoCode?: string) =>
    (price: Omit<TotalPrice, 'totalExVat'>) => {
        const { priceType, numberOfTravelers, totalIncVat } = price;
        return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            item_id: product.productId.toString(),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            item_name: product.productTitle,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            item_category: priceType,
            price: totalIncVat / numberOfTravelers,
            quantity: numberOfTravelers,
            currency,
            coupon: promoCode,
        };
    };

export function addToCart(event: AddToCartEvent) {
    return {
        items: event.products
            .map((product) => product.prices.map(mapPrice(product.product, product.currency)))
            .flat(),
        value: event.priceIncVat,
        currency: event.currency,
    };
}

export function removeFromCart(event: RemoveFromCartEvent) {
    return {
        items: event.products
            .map((product) => product.prices.map(mapPrice(product.product, product.currency)))
            .flat(),
        value: event.priceIncVat,
        currency: event.currency,
    };
}

export function startCheckout(event: StartCheckoutEvent) {
    return {
        items: event.products
            .map(({ product, prices }) => {
                return prices.map(mapPrice(product, event.currency));
            })
            .flat(),
        value: event.priceIncVat,
        currency: event.currency,
    };
}

export function paymentInfoAdded(event: PaymentInfoAddedEvent) {
    return {
        items: event.products
            .map(({ product, prices }) => {
                return prices.map(mapPrice(product, event.currency, event.promoCode?.code));
            })
            .flat(),
        value: event.priceIncVat,
        currency: event.currency,
        coupon: event.promoCode,
    };
}

export function book(event: BookEvent) {
    return {
        items: event.products
            .map(({ product, prices }) =>
                prices.map(mapPrice(product, event.currency, event.promoCode?.code)),
            )
            .flat(),
        value: event.priceIncVat,
        currency: event.currency,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        transaction_id: event.reference,
        coupon: event.promoCode,
    };
}

export function viewItem(event: ViewItemEvent) {
    return {
        items: event.products
            .map(({ product, prices }) => prices.map(mapPrice(product, event.currency)))
            .flat(),
        value: event.priceIncVat,
        currency: event.currency,
    };
}

export function checkoutStep(event: CheckoutStepEvent) {
    return {
        // TODO camelCase?
        // eslint-disable-next-line @typescript-eslint/naming-convention
        checkout_step: getStep(event.checkoutStep),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        checkout_step_title: event.checkoutStep,
    };
}

export function login(_event: AuthenticationEvent) {
    return {
        method: 'Bilberry',
    };
}

function subscriber(
    event: BilberryWidgetEvent,
    fn: (
        event:
            | 'add_to_cart'
            | 'remove_from_cart'
            | 'begin_checkout'
            | 'add_payment_info'
            | 'purchase'
            | 'view_item'
            | 'set_checkout_step'
            | 'login',
        data: any,
    ) => void,
) {
    debugLog('GA4 custom event triggered with data: ', event);

    // Handle event based on type
    switch (event.type) {
        case 'addToCart':
            return fn('add_to_cart', addToCart(event));
        case 'removeFromCart':
            return fn('remove_from_cart', removeFromCart(event));
        case 'startCheckout':
            return fn('begin_checkout', startCheckout(event));
        case 'paymentInfoAdded':
            return fn('add_payment_info', paymentInfoAdded(event));
        case 'book':
            return fn('purchase', book(event));
        case 'viewItem':
            return fn('view_item', viewItem(event));
        case 'checkoutStep':
            return fn('set_checkout_step', checkoutStep(event));
        case 'login':
            return fn('login', login(event));
    }
}

export function subscriberGtag(event: BilberryWidgetEvent) {
    subscriber(event, (event, data) => {
        if (!window.gtag) return;

        window.gtag('event', event, data);
    });
}

// Maps our camelCase customer shape to the snake_case shape Google's
// Enhanced Conversions tag reads off the dataLayer.
function toEnhancedConversionData(customer: EnhancedConversionCustomer) {
    const { email, phoneNumber, firstName, lastName, address } = customer;
    const data: Record<string, unknown> = {};
    if (email) data.email = email;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    if (phoneNumber) data.phone_number = phoneNumber;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    if (firstName) data.first_name = firstName;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    if (lastName) data.last_name = lastName;
    if (address) {
        const home: Record<string, unknown> = {};
        if (address.street) home.street = address.street;
        if (address.city) home.city = address.city;
        if (address.region) home.region = address.region;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        if (address.postalCode) home.postal_code = address.postalCode;
        if (address.country) home.country = address.country;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        if (Object.keys(home).length > 0) data.home_address = home;
    }
    return Object.keys(data).length > 0 ? data : undefined;
}

// Must run BEFORE the existing purchase push so the Enhanced Conversions
// GTM tag has the customer data on the dataLayer when it fires.
function pushEnhancedConversion(event: BilberryWidgetEvent) {
    if (!BilberryWidgetsGlobal.enhancedConversionsEnabled) return;
    if (event.type !== 'book' || !event.customer) return;

    const data = toEnhancedConversionData(event.customer);
    if (!data) return;

    window.dataLayer = window.dataLayer ?? [];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    window.dataLayer.push({ enhanced_conversion_data: data });
}

export function subscriberGTM(event: BilberryWidgetEvent) {
    pushEnhancedConversion(event);

    // Map event-names to legacy universal analytics (UA).
    // These names will not be automatically recognized by
    // GA4, but we need to allow customers to use this if
    // they have old google-tag-manager setups where they
    // are mapping against old UA-style event-names.
    const uaMapping = [
        ['add_to_cart', 'addToCart'],
        ['remove_from_cart', 'removeFromCart'],
        ['begin_checkout', 'checkout'],
        ['purchase', 'purchase'],
        ['view_item', 'productClick'],
        ['set_checkout_step', 'checkoutOption'],
        ['login', 'login'],
    ];

    // Function to map/match against old/legacy `uaMapping`.
    const mapEventToUA = (event: string) => uaMapping.find(([key]) => key === event)?.[1] || event;

    subscriber(event, (event: string, data: any) => {
        window.dataLayer = window.dataLayer ?? [];

        window.dataLayer.push({
            // If config-option `useGtmGa4` is true, use the event-name directly
            // (which is GA4 compatible), instead of mapping to the old/legacy UA style)
            event: BilberryWidgetsGlobal.useGtmGa4 ? event : mapEventToUA(event),

            // While placing data under an `ecommerce` attribute is not
            // strictly required anymore, it is per 11.08.2025 the recommended
            // practice to still use it when pushing to `dataLayer`, for backward
            // compatibility.
            ecommerce: data,
        });
    });
}
